import React, { useState, useEffect } from 'react';
import { Card, Button, Alert, Badge, ListGroup } from 'react-bootstrap';
import { calendarApi } from '../services/api';
import { FiCalendar, FiCheck, FiX, FiRefreshCw, FiTrash2, FiChrome, FiMail } from 'react-icons/fi';

const CalendarPage = () => {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchIntegrations();
    
    // Check for success/error from OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success')) {
      setSuccess(`${urlParams.get('success') === 'google' ? 'Google' : 'Outlook'} calendar connected successfully!`);
      fetchIntegrations();
    }
    if (urlParams.get('error')) {
      setError('Failed to connect calendar. Please try again.');
    }
  }, []);

  const fetchIntegrations = async () => {
    try {
      setLoading(true);
      const response = await calendarApi.getIntegrations();
      setIntegrations(response.data.data.integrations);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load calendar integrations');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectGoogle = async () => {
    try {
      setProcessing(true);
      const response = await calendarApi.initiateGoogleAuth();
      if (response.data.data.authUrl) {
        window.location.href = response.data.data.authUrl;
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to initiate Google Calendar connection');
      setProcessing(false);
    }
  };

  const handleConnectOutlook = async () => {
    try {
      setProcessing(true);
      const response = await calendarApi.initiateOutlookAuth();
      if (response.data.data.authUrl) {
        window.location.href = response.data.data.authUrl;
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to initiate Outlook Calendar connection');
      setProcessing(false);
    }
  };

  const handleDisconnect = async (integrationId) => {
    if (!window.confirm('Are you sure you want to disconnect this calendar integration?')) {
      return;
    }

    try {
      await calendarApi.disconnectIntegration(integrationId);
      await fetchIntegrations();
      setSuccess('Calendar integration disconnected successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to disconnect calendar integration');
    }
  };

  const getProviderIcon = (provider) => {
    switch (provider) {
      case 'google':
        return <FiChrome className="me-2" />;
      case 'outlook':
        return <FiMail className="me-2" />;
      default:
        return <FiCalendar className="me-2" />;
    }
  };

  const getProviderName = (provider) => {
    switch (provider) {
      case 'google':
        return 'Google Calendar';
      case 'outlook':
        return 'Outlook Calendar';
      case 'apple':
        return 'Apple Calendar';
      default:
        return provider;
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="loading-spinner" style={{ margin: '0 auto', width: '40px', height: '40px' }}></div>
        <p className="mt-3">Loading calendar integrations...</p>
      </div>
    );
  }

  const googleConnected = integrations.some(i => i.provider === 'google' && i.isActive);
  const outlookConnected = integrations.some(i => i.provider === 'outlook' && i.isActive);

  return (
    <div className="calendar-page">
      <h2 className="mb-4">Calendar Integration</h2>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Connect Your Calendar</h5>
        </Card.Header>
        <Card.Body>
          <p className="text-muted mb-4">
            Connect your calendar to automatically sync appointments. When you create or update an appointment, 
            it will be added to your connected calendars.
          </p>

          <div className="row g-3">
            <div className="col-md-6">
              <Card className="h-100">
                <Card.Body className="d-flex flex-column">
                  <div className="mb-3">
                    <FiChrome size={32} className="text-primary mb-2" />
                    <h5>Google Calendar</h5>
                    <p className="text-muted small">
                      Sync appointments with your Google Calendar
                    </p>
                  </div>
                  {googleConnected ? (
                    <Badge bg="success" className="mb-3">
                      <FiCheck className="me-1" />
                      Connected
                    </Badge>
                  ) : (
                    <Button
                      variant="primary"
                      onClick={handleConnectGoogle}
                      disabled={processing}
                      className="mt-auto"
                    >
                      <FiChrome className="me-2" />
                      Connect Google Calendar
                    </Button>
                  )}
                </Card.Body>
              </Card>
            </div>

            <div className="col-md-6">
              <Card className="h-100">
                <Card.Body className="d-flex flex-column">
                  <div className="mb-3">
                    <FiMail size={32} className="text-primary mb-2" />
                    <h5>Outlook Calendar</h5>
                    <p className="text-muted small">
                      Sync appointments with your Outlook/Microsoft Calendar
                    </p>
                  </div>
                  {outlookConnected ? (
                    <Badge bg="success" className="mb-3">
                      <FiCheck className="me-1" />
                      Connected
                    </Badge>
                  ) : (
                    <Button
                      variant="primary"
                      onClick={handleConnectOutlook}
                      disabled={processing}
                      className="mt-auto"
                    >
                      <FiMail className="me-2" />
                      Connect Outlook Calendar
                    </Button>
                  )}
                </Card.Body>
              </Card>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Connected Integrations */}
      {integrations.length > 0 && (
        <Card>
          <Card.Header>
            <h5 className="mb-0">Connected Calendars</h5>
          </Card.Header>
          <Card.Body>
            <ListGroup variant="flush">
              {integrations.map((integration) => (
                <ListGroup.Item key={integration.id} className="d-flex justify-content-between align-items-center">
                  <div>
                    {getProviderIcon(integration.provider)}
                    <strong>{getProviderName(integration.provider)}</strong>
                    {integration.isActive ? (
                      <Badge bg="success" className="ms-2">
                        Active
                      </Badge>
                    ) : (
                      <Badge bg="secondary" className="ms-2">
                        Inactive
                      </Badge>
                    )}
                    <div className="text-muted small mt-1">
                      Connected {new Date(integration.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDisconnect(integration.id)}
                  >
                    <FiTrash2 className="me-1" />
                    Disconnect
                  </Button>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Card.Body>
        </Card>
      )}

      {/* Info Card */}
      <Card className="mt-4 bg-light">
        <Card.Body>
          <h6>How It Works</h6>
          <ul className="mb-0">
            <li>When you create a new appointment, it will automatically be added to your connected calendars</li>
            <li>Appointment updates (time changes, cancellations) will sync to your calendars</li>
            <li>You can connect multiple calendars (Google and Outlook simultaneously)</li>
            <li>Calendar events include customer details, service information, and appointment notes</li>
          </ul>
        </Card.Body>
      </Card>
    </div>
  );
};

export default CalendarPage;
